import { doc, getDoc, addDoc, collection, runTransaction, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../index";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from "../../../spinner";
import Cookies from 'js-cookie';

// Type labels for displaying node types in Hebrew
const typeLabels = {
    nose: "נושא",
    helek: "חלק",
    kereh: "כרך",
    siman: "סימן",
    saif: "סעיף",
    perek: "פרק",
    daf: "דף",
    mizmor: "מזמור",
    tora: "תורה",
    shaar: "שער",
    masehet: "מסכת",
    sefer: "ספר",
    halahot: "הלכות",
    seder: "סדר",
    parashat: "פרשת",
    mishna: "משנה",
    alia: "עליה"
};

// Helper function to get all leaf node IDs from a subtree
const getLeafNodeIds = (node) => {
    if (!node.children || node.children.length === 0) {
        return [node.id];
    }
    let ids = [];
    node.children.forEach(child => {
        ids = ids.concat(getLeafNodeIds(child));
    });
    return ids;
};


// Tree selection node component
const SelectionTreeNode = ({
    node,
    depth = 0,
    selectedPieces,
    takenPiecesMap,
    onToggleSelection,
    isParentSelected,
    treeData,
    filterStillFree
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isLeaf = !hasChildren;

    // Check if this leaf is taken (by someone else)
    const isTaken = isLeaf && takenPiecesMap[node.id];

    // Check if this leaf is selected by current user
    const isSelected = isLeaf && selectedPieces.includes(node.id);

    // For parent nodes, check if all available children are selected
    const getSelectionStatus = () => {
        if (isLeaf) {
            return { allSelected: isSelected, someSelected: isSelected, allTaken: isTaken };
        }

        const leafIds = getLeafNodeIds(node);
        const availableLeafs = leafIds.filter(id => !takenPiecesMap[id]);
        const selectedLeafs = leafIds.filter(id => selectedPieces.includes(id));
        const takenLeafs = leafIds.filter(id => takenPiecesMap[id]);

        return {
            allSelected: availableLeafs.length > 0 && availableLeafs.every(id => selectedPieces.includes(id)),
            someSelected: selectedLeafs.length > 0,
            allTaken: takenLeafs.length === leafIds.length,
            availableCount: availableLeafs.length,
            selectedCount: selectedLeafs.length,
            totalCount: leafIds.length,
            takenCount: takenLeafs.length
        };
    };

    const status = getSelectionStatus();

    // Handle selection of parent (select all available children)
    const handleParentSelection = () => {
        if (status.allTaken) return;

        const leafIds = getLeafNodeIds(node);
        const availableLeafs = leafIds.filter(id => !takenPiecesMap[id]);

        if (status.allSelected) {
            // Deselect all
            availableLeafs.forEach(id => {
                if (selectedPieces.includes(id)) {
                    onToggleSelection(id);
                }
            });
        } else {
            // Select all available
            availableLeafs.forEach(id => {
                if (!selectedPieces.includes(id)) {
                    onToggleSelection(id);
                }
            });
        }
    };

    const getNodeDisplayName = () => {
        let name = node.name || '';
        if (node.type === 'daf' && node.dafFrom && node.dafTo) {
            name += ` (${node.dafFrom} - ${node.dafTo})`;
        }
        return name;
    };

    const getHeaderClass = () => {
        let cls = 'aher-selection-node-header';
        if (isLeaf) {
            cls += ' aher-selection-leaf';
            if (isTaken) cls += ' aher-selection-taken';
            else if (isSelected) cls += ' aher-selection-selected';
        } else {
            cls += ' aher-selection-parent';
            if (status.allTaken) cls += ' aher-selection-taken';
            else if (status.allSelected) cls += ' aher-selection-selected';
            else if (status.someSelected) cls += ' aher-selection-partial';
        }
        return cls;
    };

    return (
        <div className={`aher-selection-node ${hasChildren ? 'aher-selection-node-parent' : ''}`} style={{ marginRight: `${depth * 15}px` }} data-depth={depth}>
            <div className={getHeaderClass()}>
                {hasChildren && (
                    <span
                        className={`aher-selection-expand-icon ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </span>
                )}

                {isLeaf ? (
                    <label className="aher-selection-checkbox-container">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isTaken}
                            onChange={() => onToggleSelection(node.id)}
                        />
                        <span className="aher-selection-checkmark"></span>
                    </label>
                ) : (
                    <label className="aher-selection-checkbox-container">
                        <input
                            type="checkbox"
                            checked={status.allSelected}
                            disabled={status.allTaken}
                            onChange={handleParentSelection}
                            ref={el => {
                                if (el) {
                                    el.indeterminate = status.someSelected && !status.allSelected;
                                }
                            }}
                        />
                        <span className="aher-selection-checkmark"></span>
                    </label>
                )}

                <span className="aher-selection-type-badge">{typeLabels[node.type] || node.type}</span>
                <span className="aher-selection-node-name">{getNodeDisplayName()}</span>

                {isLeaf && isTaken && (
                    <span className="aher-selection-taken-badge">נתפס</span>
                )}

                {!isLeaf && (
                    <span className="aher-selection-stats">
                        {!filterStillFree && <span className="badge bg-secondary">{status.takenCount}/{status.totalCount} נתפסו</span>}
                        {filterStillFree && <span className="badge bg-secondary">{status.totalCount} פנויים</span>}
                        {status.selectedCount > 0 && <span className="badge bg-primary">{status.selectedCount}/{status.availableCount} נבחרו</span>}
                    </span>
                )}
            </div>

            {hasChildren && isExpanded && (
                <div className="aher-selection-children">
                    {node.children.map((child, idx) => (
                        <SelectionTreeNode
                            key={child.id || idx}
                            node={child}
                            depth={depth + 1}
                            selectedPieces={selectedPieces}
                            takenPiecesMap={takenPiecesMap}
                            onToggleSelection={onToggleSelection}
                            isParentSelected={status.allSelected}
                            treeData={treeData}
                            filterStillFree={filterStillFree}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const AddUserAher = () => {
    const params = useParams();
    const navigate = useNavigate();

    // Sium and mapping data
    const [sium, setSium] = useState(null);
    const [mappingData, setMappingData] = useState(null);

    // User form data
    const [fullname, setFullname] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [nishmat, setNishmat] = useState('');

    // Selection state
    const [selectedPieces, setSelectedPieces] = useState([]);

    // UI state
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showContactWarning, setShowContactWarning] = useState(false);
    const [sharingUser, setSharingUser] = useState(null);
    const [newUser, setNewUser] = useState(null);
    const [oldSiumUsers, setOldSiumUsers] = useState([]);
    const [hasOldShas, setHasOldShas] = useState(false);
    const [showOldUsersModal, setShowOldUsersModal] = useState(false);
    const [oldUsersSearch, setOldUsersSearch] = useState('');
    const [showNishmatInput, setShowNishmatInput] = useState(false);
    // Filter for selection tree - show only still-free pieces
    const [filterStillFree, setFilterStillFree] = useState(true);

    const isContactPickerSupported = 'contacts' in navigator && 'ContactsManager' in window;

    // Create a map of taken pieces for quick lookup
    const takenPiecesMap = useMemo(() => {
        const map = {};
        if (sium?.learningPieces) {
            sium.learningPieces.forEach(piece => {
                if (piece.taken) {
                    map[piece.id] = true;
                }
            });
        }
        return map;
    }, [sium?.learningPieces]);

    // Map of learning pieces by id for filtering
    const learningPiecesMap = useMemo(() => {
        const map = {};
        if (sium?.learningPieces) {
            sium.learningPieces.forEach(p => { map[p.id] = p; });
        }
        return map;
    }, [sium?.learningPieces]);

    // Load sium and mapping data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const siumRef = doc(db, "siumimAher", params.siumId);
                const siumDoc = await getDoc(siumRef);

                if (siumDoc.exists()) {
                    const siumData = siumDoc.data();
                    setSium(siumData);

                    const isAdminCheck = Cookies.get('siumShasId') === params.siumId;
                    setIsAdmin(isAdminCheck);

                    // Fetch mapping data
                    if (siumData.mipuiId) {
                        const mappingRef = doc(db, "mipui", siumData.mipuiId);
                        const mappingDoc = await getDoc(mappingRef);
                        if (mappingDoc.exists()) {
                            setMappingData(mappingDoc.data());
                        }
                    }

                    // Fetch old sium users if admin
                    if (isAdminCheck && siumData.organizerPhone && siumData.organizerEmail) {
                        // Query old siumim from both collections
                        const oldSiumsQuery = query(
                            collection(db, 'siumim'),
                            where('organizerPhone', '==', siumData.organizerPhone),
                            where('organizerEmail', '==', siumData.organizerEmail)
                        );

                        const oldSiumsAherQuery = query(
                            collection(db, 'siumimAher'),
                            where('organizerPhone', '==', siumData.organizerPhone),
                            where('organizerEmail', '==', siumData.organizerEmail)
                        );

                        const [oldSiumsSnapshot, oldSiumsAherSnapshot] = await Promise.all([
                            getDocs(oldSiumsQuery),
                            getDocs(oldSiumsAherQuery)
                        ]);

                        const oldSiumIds = oldSiumsSnapshot.docs.map(doc => doc.id);
                        const oldSiumAherIds = oldSiumsAherSnapshot.docs.map(doc => doc.id)

                        const allUsers = [];

                        // Fetch users from regular siumim
                        if (oldSiumIds.length > 0) {
                            const usersQuery = query(
                                collection(db, 'users'),
                                where('siumId', 'in', oldSiumIds)
                            );
                            const usersSnapshot = await getDocs(usersQuery);
                            usersSnapshot.docs.forEach(doc => {
                                allUsers.push(doc.data());
                            });
                        }

                        // Fetch users from siumimAher
                        if (oldSiumAherIds.length > 0) {
                            const usersAherQuery = query(
                                collection(db, 'usersAher'),
                                where('siumId', 'in', oldSiumAherIds)
                            );
                            const usersAherSnapshot = await getDocs(usersAherQuery);
                            usersAherSnapshot.docs.forEach(doc => {
                                allUsers.push(doc.data());
                            });
                        }

                        // Remove duplicates
                        const uniqueUsers = [];
                        const seenPhones = new Set();
                        const seenEmails = new Set();
                        const seenNames = new Set();

                        allUsers.forEach(userData => {
                            const isDuplicate =
                                (userData.phone && seenPhones.has(userData.phone)) ||
                                (userData.email && seenEmails.has(userData.email)) ||
                                (userData.fullName && seenNames.has(userData.fullName));

                            if (!isDuplicate) {
                                uniqueUsers.push(userData);
                                if (userData.phone) seenPhones.add(userData.phone);
                                if (userData.email) seenEmails.add(userData.email);
                                if (userData.fullName) seenNames.add(userData.fullName);
                            }
                        });

                        const sortedUsers = uniqueUsers.sort((a, b) => {
                            const nameA = (a.fullName || '').trim();
                            const nameB = (b.fullName || '').trim();
                            return nameA.localeCompare(nameB, 'he');
                        });

                        setOldSiumUsers(sortedUsers);
                        setHasOldShas(uniqueUsers.length > 0);
                    }
                } else {
                    navigate("/");
                }
            } catch (e) {
                console.error("Error fetching sium:", e);
                navigate("/");
            }
        };

        fetchData();
    }, [params.siumId, navigate]);

    // Toggle selection of a piece
    const handleToggleSelection = (pieceId) => {
        setSelectedPieces(prev => {
            if (prev.includes(pieceId)) {
                return prev.filter(id => id !== pieceId);
            } else {
                return [...prev, pieceId];
            }
        });
    };

    // Handle filter change (only still-free implemented)
    const handleFilterChange = (value) => {
        setFilterStillFree(value);
    };

    // Display error message
    const displayErrorMessage = (reset, errorMsg) => {
        if (reset) {
            setSelectedPieces([]);
        }
        setErrorMessage(errorMsg);
        const toastModal = document.getElementById('errorModal');
        if (toastModal) {
            const toastBootstrap = window.bootstrap.Toast.getOrCreateInstance(toastModal);
            toastBootstrap.show();
        }
    };

    // Validate and update sium with transaction
    const validateAndUpdateSium = async () => {
        const docRef = doc(db, "siumimAher", params.siumId);
        setErrorMessage("");

        try {
            return await runTransaction(db, async (transaction) => {
                const siumDoc = await transaction.get(docRef);

                if (!siumDoc.exists()) {
                    displayErrorMessage(true, "הסיום לא נמצא");
                    return null;
                }

                const siumObj = siumDoc.data();
                setSium(siumObj);

                // Check if selected pieces are still available
                const learningPieces = siumObj.learningPieces || [];
                const learningPiecesMap = {};
                learningPieces.forEach(piece => {
                    learningPiecesMap[piece.id] = piece;
                });

                if (selectedPieces.length === 0) {
                    displayErrorMessage(false, "לא נבחרו חלקים ללימוד");
                    return null;
                }

                // Validate all selected pieces are available
                for (const pieceId of selectedPieces) {
                    const piece = learningPiecesMap[pieceId];
                    if (!piece) {
                        displayErrorMessage(true, "אחד החלקים שנבחרו לא קיים");
                        return null;
                    }
                    if (piece.taken) {
                        displayErrorMessage(true, "אחד החלקים שנבחרו כבר נתפס, נסה לבחור שוב");
                        return null;
                    }
                }

                // Update learning pieces - mark selected as taken
                siumObj.learningPieces = learningPieces.map(piece => {
                    if (selectedPieces.includes(piece.id)) {
                        return { ...piece, taken: true };
                    }
                    return piece;
                });

                siumObj.lastAccessed = new Date().getTime();
                transaction.update(docRef, siumObj);

                return siumObj;
            });
        } catch (e) {
            console.error("Transaction failed:", e);
            displayErrorMessage(false, "ישנו כעת עומס בבחירת החלקים, נסה בעוד מספר שניות");
            return null;
        }
    };

    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
    const [duplicateDetails, setDuplicateDetails] = useState([]);

    // Check if a user with the same phone or email already exists in this sium
    const checkDuplicateContact = async () => {
        try {
            const cleanPhone = phone ? phone.replace(/\D/g, '') : '';
            const cleanEmail = email ? email.trim().toLowerCase() : '';
            if (!cleanPhone && !cleanEmail) return true; // nothing to check

            const usersRef = collection(db, "usersAher");
            const usersQuery = query(usersRef, where("siumId", "==", params.siumId));
            const snapshot = await getDocs(usersQuery);

            const duplicates = [];
            snapshot.docs.forEach(doc => {
                const userData = doc.data();
                if (cleanPhone && userData.phone && userData.phone.replace(/\D/g, '') === cleanPhone) {
                    duplicates.push(`טלפון ${cleanPhone} (${userData.fullName || 'ללא שם'})`);
                }
                if (cleanEmail && userData.email && userData.email.trim().toLowerCase() === cleanEmail) {
                    duplicates.push(`אימייל ${cleanEmail} (${userData.fullName || 'ללא שם'})`);
                }
            });

            if (duplicates.length > 0) {
                setDuplicateDetails(duplicates);
                setShowDuplicateWarning(true);
                return false;
            }
            return true;
        } catch (e) {
            console.error("Error checking duplicate contact:", e);
            return true; // allow to continue on error
        }
    };

    const handleDuplicateProceed = () => {
        setShowDuplicateWarning(false);
        handleSubmit(new Event('submit', { cancelable: true, bubbles: true }));
    };

    // Handle form submission with validation
    const handleSubmitWithValidation = async (event) => {
        event.preventDefault();

        if (!phone && !email) {
            setShowContactWarning(true);
            return;
        }

        const canProceed = await checkDuplicateContact();
        if (!canProceed) return;

        handleSubmit(event);
    };

    // Handle adding anyway (without contact info)
    const handleAddAnyway = () => {
        setShowContactWarning(false);
        handleSubmit(new Event('submit', { cancelable: true, bubbles: true }));
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const updatedSium = await validateAndUpdateSium();

            if (updatedSium) {
                const cleanedEmail = email ? String(email).trim().toLowerCase() : '';
                // Create user object
                const newUserObj = {
                    email: cleanedEmail,
                    phone: phone.replace(/\D/g, ''),
                    fullName: fullname,
                    nishmat: nishmat,
                    siumId: params.siumId,
                    mipuiId: sium.mipuiId,
                    siumType: 'aher',
                    lastModified: new Date(),
                    pieces: selectedPieces.reduce((acc, id) => {
                        acc[id] = false; // false = not learned yet
                        return acc;
                    }, {}),
                    piecesCount: selectedPieces.length
                };

                setNewUser(newUserObj);

                const docRef = await addDoc(collection(db, "usersAher"), newUserObj);

                if (isAdmin) {
                    setSharingUser(docRef);
                    setTimeout(() => {
                        const myModal = document.getElementById('sharingModal');
                        if (myModal) {
                            const modal = new window.bootstrap.Modal(myModal);
                            modal.show();
                        }
                    }, 100);
                } else {
                    // Navigate to user status page (to be implemented)
                    navigate("/userStatusAher/" + docRef.id);
                }
            } else {
                if (!errorMessage) {
                    displayErrorMessage(false, "ישנו כעת עומס בבחירת החלקים, נסה בעוד מספר שניות");
                }
            }
        } catch (e) {
            console.error("Error adding user:", e);
            displayErrorMessage(false, "שגיאה בהוספת הלומד");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle phone change - only allow digits
    const handlePhoneChange = (e) => {
        const numericValue = e.target.value.replace(/\D/g, '');
        setPhone(numericValue);
    };

    // Handle picking contact from device
    const handlePickContact = async () => {
        try {
            // Check if Contact Picker API is supported
            if (!('contacts' in navigator) || !('ContactsManager' in window)) {
                alert('הדפדפן שלך לא תומך בגישה לאנשי קשר. נסה לשנות הרשאות או להשתמש בדפדפן Chrome.');
                return;
            }

            const props = ['name', 'tel', 'email'];
            const opts = { multiple: false };

            const contacts = await navigator.contacts.select(props, opts);

            if (contacts && contacts.length > 0) {
                const contact = contacts[0];

                // Set name
                if (contact.name && contact.name.length > 0) {
                    setFullname(contact.name[0]);
                }

                // Set phone - handle country prefix based on sium.phonePrefix
                if (contact.tel && contact.tel.length > 0) {
                    let contactPhone = contact.tel[0].replace(/\D/g, '');
                    const countryPrefix = sium?.phonePrefix || '972'; // Default to Israeli prefix

                    // Remove country prefix if present
                    if (contactPhone.startsWith(countryPrefix)) {
                        contactPhone = contactPhone.substring(countryPrefix.length);
                        // For Israeli numbers, ensure it starts with 0
                        if (countryPrefix === '972' && !contactPhone.startsWith('0')) {
                            contactPhone = '0' + contactPhone;
                        }
                    }

                    setPhone(contactPhone);
                }

                // Set email
                if (contact.email && contact.email.length > 0) {
                    setEmail(contact.email[0]);
                }
            }
        } catch (err) {
            // User cancelled the picker or permission denied
            if (err.name !== 'InvalidStateError' && err.name !== 'SecurityError') {
                console.error('Error picking contact:', err);
                alert('לא ניתן לגשת לאנשי הקשר. ודא שנתת הרשאה לאתר.');
            }
        }
    };

    // Handle selecting user from old sium
    const handleSelectOldUser = (user) => {
        setPhone(user.phone || '');
        setEmail(user.email || '');
        setFullname(user.fullName || '');
        setNishmat(user.nishmat || '');
        setShowOldUsersModal(false);
    };

    // Share page
    const sharePage = () => {
        if (navigator.share) {
            navigator.share({
                title: `סיום ${bookName} של "${sium.kehilaName}"`,
                text: 'הוספת לומד חדש',
                url: '/addUserAher/' + params.siumId
            });
        }
    };

    // General sharing for new user
    const generalSharing = () => {
        if (navigator.share && sharingUser) {
            navigator.share({
                title: `סיום ${bookName} של "${sium.kehilaName}"`,
                text: newUser?.fullName || '',
                url: '/userStatusAher/' + sharingUser.id
            });
        }
    };

    // WhatsApp sharing
    const whatsappSharing = () => {
        if (sharingUser) {
            const countryCode = sium?.phonePrefix || '972';
            let cleanPhone = newUser.phone.replace(/\D/g, '');
            if (cleanPhone.startsWith(countryCode)) {
                cleanPhone = cleanPhone.substring(countryCode.length);
            }
            if (cleanPhone.startsWith('0')) {
                cleanPhone = cleanPhone.substring(1);
            }
            const phone = countryCode + cleanPhone;

            let message = `שלום ${newUser.fullName}`;
            message += `\n\nניתן לעדכן את מצב הלמידה שלך בקישור הבא:\nhttps://siumshas.co.il/userStatusAher/${sharingUser.id}`;
            const whatsappLink = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
            window.open(whatsappLink, '_blank');
        }
    };

    // Loading state
    if (!sium || !mappingData) {
        return <Spinner />;
    }

    const bookName = mappingData?.bookName || "ספר";
    const today = new Date();
    const endD = sium.endSium.toDate();
    const daysNum = Math.round((endD - today) / (24 * 60 * 60 * 1000));

    // Filter tree data to hide taken leaves when filterStillFree is true
    const filterTreeData = (nodes) => {
        if (!filterStillFree) return nodes;

        const filterNode = (node) => {
            // Leaf node
            if (!node.children || node.children.length === 0) {
                const piece = learningPiecesMap[node.id];
                const isTaken = piece?.taken;
                if (isTaken) return null;
                return node;
            }

            // Parent - recursively filter children
            const filteredChildren = node.children
                .map(child => filterNode(child))
                .filter(child => child !== null);

            if (filteredChildren.length === 0) return null;
            return { ...node, children: filteredChildren };
        };

        return nodes.map(n => filterNode(n)).filter(n => n !== null);
    };

    return (
        <div className="sium-main">
            <div className="sium-main-container">
                <div className="basad-with-return">
                    <div className="basad">
                        <span>בס"ד</span>
                        <br/>
                        <span>להצלחת עם ישראל</span>
                    </div>
                    {isAdmin && (
                        <div className="return-container">
                            <a className="btn sium-button return-button"
                               href={"/niulSiumAher/" + params.siumId + "/siumStatus"}>חזור</a>
                        </div>
                    )}
                </div>

                <div className="sium-logo">
                    <a className="sium-logo-img" href="/" aria-label="Main page"><span className="visually-hidden">דף הבית</span></a>
                </div>

                <div className="grid p-3 text-primary-emphasis bg-primary-subtle border border-primary-subtle rounded-3 sium-titles">
                    {sium.nishmat && <span>הלימוד {sium.nishmat}</span>}
                    <h2>חלוקת {bookName} של {sium.kehilaName}</h2>
                    <h4>הוספת לומד חדש</h4>
                    {sium.logo && <div className={sium.logo} role="img" aria-label="לוגו"/>}
                    {daysNum > 0 && <p className="bold days-left">נותרו עוד {daysNum} ימים</p>}
                    {daysNum <= 0 && daysNum > -30 && <p className="bold days-left">הסיום הסתיים!</p>}
                </div>

                {isAdmin && (
                    <div className="gap-2 col-8 mx-auto sharing-tip">
                        <svg xmlns="http://www.w3.org/2000/svg" width="2.66666in" height="2.66666in" version="1.1"
                             viewBox="0 0 2667 2667" className="col-1 gap-2">
                            <g id="Layer_x0020_1">
                                <circle className="fil0" cx="1333" cy="1333" r="1333" id="id_101"></circle>
                                <path className="fil1"
                                      d="M1140 2086l-263 0c-13,0 -23,-10 -23,-23 0,-13 10,-23 23,-23l263 0c12,0 22,10 22,23 0,13 -10,23 -22,23zm-498 -1349c-8,0 -16,-4 -20,-12l-57 -98c-6,-10 -2,-24 9,-31 11,-6 25,-2 31,9l56 98c7,10 3,24 -8,31 -4,2 -7,3 -11,3zm176 -74c-10,0 -19,-6 -22,-16l-29 -109c-3,-13 4,-25 16,-28 12,-3 25,4 28,16l29 109c3,12 -4,24 -16,28 -2,0 -4,0 -6,0zm190 -24c-13,0 -23,-11 -23,-23l0 -113c0,-13 10,-23 23,-23 12,0 23,10 23,23l0 113c0,12 -11,23 -23,23zm356 693c-3,0 -7,-1 -10,-2 -8,-4 -13,-12 -13,-21l1 -167 -46 0c-12,0 -23,-11 -23,-23l0 -560c0,-13 11,-23 23,-23l820 0c13,0 23,10 23,23l0 560c0,12 -10,23 -23,23l-502 0 -236 185c-4,3 -9,5 -14,5zm633 -366c7,0 12,-6 12,-13 0,-7 -5,-13 -12,-13l-581 0c-7,0 -13,6 -13,13 0,7 6,13 13,13l581 0zm0 -124c7,0 12,-6 12,-13 0,-7 -5,-12 -12,-12l-581 0c-7,0 -13,5 -13,12 0,7 6,13 13,13l581 0zm-75 -123c7,0 12,-6 12,-13 0,-7 -5,-13 -12,-13l-431 0c-7,0 -13,6 -13,13 0,7 6,13 13,13l431 0zm-733 1259l-361 0c-13,0 -23,-10 -23,-23 0,-358 -83,-424 -157,-483 -62,-49 -120,-95 -120,-273 0,-265 215,-481 480,-481 84,0 167,23 239,64l0 337c0,27 22,49 49,49l19 0 0 141c0,19 10,36 27,45 7,3 14,5 22,5 11,0 22,-4 30,-11l90 -70c-15,115 -64,154 -116,194 -73,59 -157,125 -157,483 0,13 -10,23 -22,23zm-549 -779c0,61 2,121 52,165 10,9 21,17 32,25 11,10 23,19 34,29 33,30 60,64 82,103 2,4 6,6 11,6 2,0 4,0 6,-1 6,-3 8,-11 5,-17 -23,-42 -52,-78 -87,-110 -11,-10 -23,-20 -36,-30 -10,-8 -20,-16 -30,-24 -43,-38 -43,-93 -43,-146 0,-189 153,-343 342,-343 63,0 125,18 179,51 6,3 14,1 18,-5 3,-6 1,-13 -4,-17 -58,-36 -125,-54 -193,-54 -203,0 -368,165 -368,368zm431 988l-126 0c-12,0 -22,-11 -22,-23 0,-13 10,-23 22,-23l126 0c12,0 23,10 23,23 0,12 -10,23 -23,23z"
                                      id="id_102"></path>
                            </g>
                        </svg>
                        <span>שתף עם החבר'ה שיוסיפו את עצמם</span>
                        <button className="btn" type="button" onClick={sharePage}>שתף</button>
                    </div>
                )}

                {isContactPickerSupported && (
                    <div className="add-user-from-contacts">
                        <button className="btn add-user-from-contacts" type="button" onClick={handlePickContact}>
                            הוספת לומד מאנשי הקשר
                        </button>
                    </div>
                )}

                {isAdmin && hasOldShas && (
                    <div className="add-user-from-contacts">
                        <button className="btn add-user-from-contacts" type="button" onClick={() => setShowOldUsersModal(true)}>
                            הוספת לומד מסיום קודם
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmitWithValidation}>
                    <input
                        className="form-control"
                        type="text"
                        placeholder="שם מלא"
                        name="fullName"
                        value={fullname}
                        onChange={(e) => setFullname(e.target.value)}
                        aria-label="Full name"
                        required={true}
                    />
                    <input
                        className="form-control"
                        type="text"
                        placeholder="פלאפון"
                        name="phone"
                        value={phone}
                        onChange={handlePhoneChange}
                        aria-label="Phone"
                    />
                    <input
                        className="form-control"
                        type="text"
                        placeholder="אימייל"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        aria-label="Email"
                    />

                    {!showNishmatInput ? (
                        <div className="form-check nishmat-checkbox">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showNishmatCheckbox"
                                onChange={() => setShowNishmatInput(true)}
                            />
                            <label className="form-check-label" htmlFor="showNishmatCheckbox">
                                להוסיף לעילוי נשמת/לרפואת/להצלחת
                            </label>
                        </div>
                    ) : (
                        <input
                            className="form-control"
                            type="text"
                            placeholder="לעילוי נשמת/לרפואת/להצלחת"
                            value={nishmat}
                            onChange={(e) => setNishmat(e.target.value)}
                            aria-label="Nishmat"
                        />
                    )}

                    {/* Book selection tree */}
                    <div className="aher-selection-container">
                        <div className="aher-selection-header">
                            <h5>בחר חלקים ללימוד</h5>
                            <span className="aher-selection-count">
                                {selectedPieces.length} חלקים נבחרו
                            </span>
                        </div>

                        {/* Filter checkbox - use same classes as BookStatus for consistent design */}
                        <div className="masehtot-filter-container margin-top-10 margin-bottom-10">
                            <label className="masehtot-filter-label">סינון:</label>
                            <div className="filter-new-user">
                                <input
                                    className="filter-checkbox"
                                    type="checkbox"
                                    id="filterStillFreeAherAddUser"
                                    checked={filterStillFree}
                                    onChange={(e) => handleFilterChange(e.target.checked)}
                                />
                                <label className="" htmlFor="filterStillFreeAherAddUser">
                                    הצג רק מה שפנוי
                                </label>
                            </div>
                        </div>

                        <div className="aher-selection-legend">
                            <div className="aher-legend-item">
                                <span className="aher-legend-box aher-legend-available"></span>
                                <span>פנוי</span>
                            </div>
                            <div className="aher-legend-item">
                                <span className="aher-legend-box aher-legend-selected"></span>
                                <span>נבחר</span>
                            </div>
                            <div className="aher-legend-item">
                                <span className="aher-legend-box aher-legend-taken"></span>
                                <span>נתפס</span>
                            </div>
                        </div>

                        <div className="aher-selection-tree">
                            {mappingData.treeData && filterTreeData(mappingData.treeData).map((node, idx) => (
                                <SelectionTreeNode
                                    key={node.id || idx}
                                    node={node}
                                    depth={0}
                                    selectedPieces={selectedPieces}
                                    takenPiecesMap={takenPiecesMap}
                                    onToggleSelection={handleToggleSelection}
                                    isParentSelected={false}
                                    treeData={mappingData.treeData}
                                    filterStillFree={filterStillFree}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Error toast */}
                    <div className="toast-container position-fixed top-0 start-50 translate-middle-x">
                        <div id="errorModal" className="toast" role="alert" aria-live="assertive"
                             aria-atomic="true" data-bs-autohide="true" data-bs-delay="3000">
                            <button type="button" className="btn-close me-2 m-auto" data-bs-dismiss="toast"
                                    aria-label="Close"></button>
                            <div className="toast-body">
                                {errorMessage}
                            </div>
                        </div>
                    </div>

                    {/* Submit button */}
                    {selectedPieces.length > 0 && !isSubmitting && (
                        <div className="sium-button-container sticky-bottom">
                            <button className="btn sium-button" type="submit">הוסף לומד</button>
                        </div>
                    )}
                    {isSubmitting && (
                        <div className="sium-button-container sticky-bottom">
                            <button type="button" className="btn sium-button">
                                <div className="updateStatusSpinner"></div>
                            </button>
                        </div>
                    )}
                </form>

                {/* Sharing modal */}
                <div className="modal fade modal-dialog-scrollable" id="sharingModal" data-bs-backdrop="static"
                     data-bs-keyboard="false" tabIndex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
                    {sharingUser && newUser && (
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h1 className="modal-title fs-5">{newUser.fullName} נוסף בהצלחה!</h1>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal"
                                            aria-label="Close" onClick={() => window.location.reload(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        <span>יצרנו לינק ללומד על מנת שיוכל לעדכן את מצב הלימוד שלו, תצטרך לשתף איתו את הלינק.</span>
                                    </p>
                                    <p>
                                        <span>אם תשתף עם הלומדים את דף "הוסף לומד" והם יוסיפו את עצמם, לא תצטרך לשתף את הלינקים</span>
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal"
                                            onClick={() => window.location.reload(false)}>סגור</button>
                                    <button type="button" className="btn btn-primary"
                                            onClick={generalSharing}>שתף</button>
                                    <button type="button" className="btn btn-primary"
                                            onClick={whatsappSharing}>whatsapp</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Contact warning modal */}
                {showContactWarning && (
                    <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">חסרים פרטי התקשרות</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowContactWarning(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        {isAdmin
                                            ? "מומלץ להזין טלפון או אימייל, כדי שתוכל ליצור קשר עם הלומד החדש"
                                            : 'מומלץ להזין טלפון או אימייל, כדי שמנהל הסיום יוכל ליצור איתך קשר'}
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleAddAnyway}>
                                        הוסף לומד בכל זאת
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={() => setShowContactWarning(false)}>
                                        אוסיף פרטי התקשרות
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Duplicate contact warning modal */}
                {showDuplicateWarning && (
                    <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">שים לב - פרטים כפולים</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowDuplicateWarning(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p>קיים כבר לומד עם אותם פרטים בסיום הזה:</p>
                                    <ul className="list-group list-group-flush">
                                        {duplicateDetails.map((detail, index) => (
                                            <li key={index} className="list-group-item">{detail}</li>
                                        ))}
                                    </ul>
                                    <p className="mt-3">האם להמשיך בכל זאת?</p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowDuplicateWarning(false)}>
                                        חזור לעריכה
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleDuplicateProceed}>
                                        המשך בכל זאת
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Old users modal */}
                {showOldUsersModal && (
                    <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">בחר לומד מסיום קודם</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowOldUsersModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="learners-search-container margin-bottom-10">
                                        {!oldUsersSearch && <span className="learners-search-icon">🔍</span>}
                                        <input
                                            type="text"
                                            className="form-control learners-search-input"
                                            placeholder="חיפוש לפי שם, טלפון, אימייל..."
                                            value={oldUsersSearch}
                                            onChange={(e) => setOldUsersSearch(e.target.value)}
                                        />
                                        {oldUsersSearch && (
                                            <button
                                                type="button"
                                                className="learners-search-clear"
                                                onClick={() => setOldUsersSearch('')}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    <div className="list-group">
                                        {oldSiumUsers
                                            .filter(user => {
                                                if (!oldUsersSearch.trim()) return true;
                                                const q = oldUsersSearch.toLowerCase();
                                                return (
                                                    (user.fullName && user.fullName.toLowerCase().includes(q)) ||
                                                    (user.phone && user.phone.includes(q)) ||
                                                    (user.email && user.email.toLowerCase().includes(q))
                                                );
                                            })
                                            .map((user, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="list-group-item list-group-item-action"
                                                onClick={() => handleSelectOldUser(user)}
                                            >
                                                <div className="d-flex w-100 justify-content-between">
                                                    <h6 className="mb-1">{user.fullName}</h6>
                                                </div>
                                                {user.phone && <small className="text-muted">{user.phone}</small>}
                                                {user.phone && user.email && <span className="text-muted"> | </span>}
                                                {user.email && <small className="text-muted">{user.email}</small>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowOldUsersModal(false)}>
                                        סגור
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="sium-bottom-page margin-top-20">
                    <p><span> © כל הזכויות שמורות לאתר סיום הש"ס</span></p>
                </div>
            </div>
        </div>
    );
};

export default AddUserAher;
